import React, { RefObject } from 'react';
import parse from 'html-react-parser';
import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Skeleton } from '@mui/material';

interface Tag {
  title: string;
  color: string;
}

export interface CreativeCard {
  id?: string | number;
  documentId?: string;
  title: string;
  description: string;
  datePosted: string;
  tags: Tag[];
  views: string;
  image?: string;
  creativeType?: string;
  category?: string;
  sessionDetails?: any;
  contact?: any;
  actions?: string[];
  thumbnail?: any;
}

interface MarketingCreativeModalProps {
  card: CreativeCard;
  isModalLoading?: boolean;
  viewCountOverride?: string | null;
  onClose: () => void;
  onWhatsAppShare?: () => void;
  onEmailShare?: () => void;
  onCopyToClipboard?: () => void;
  onDownload?: () => void;
  captureRef?: RefObject<HTMLDivElement>;
  profileData?: any;
  referralCode?: string;
}

const getBadgeColor = (color: string) => {
  return color === 'red' ? 'bg-red-600' : 'bg-green-600';
};

const MarketingCreativeModal: React.FC<MarketingCreativeModalProps> = ({
  card,
  isModalLoading,
  viewCountOverride,
  onClose,
  onWhatsAppShare,
  onEmailShare,
  onCopyToClipboard,
  onDownload,
  captureRef,
  profileData,
  referralCode,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-[#fafafa] rounded-lg max-w-4xl w-full mx-4 overflow-y-auto max-h-[90vh] relative">
        {/* Close Button */}
        <CloseIcon
          className="absolute top-5 right-5 font-bold text-neutral-500 hover:text-black cursor-pointer"
          onClick={onClose}
          fontSize={'small'}
        />
        {/* Content Section */}
        <div className="flex flex-col items-center sm:flex-row h-full">
          {/* Left: Image */}
          <div
            ref={captureRef}
            className={`bg-white rounded-lg gap-2 flex flex-col p-[14px] w-[300px] items-center m-9`}
            style={{
              boxShadow: `
                0px 1.1px 2.21px 0px #00000005,
                0px 5.52px 5.52px 0px #00000003,
                0px 11.04px 6.62px 0px #00000003,
                0px 19.87px 7.73px 0px #00000000,
                0px 30.91px 8.83px 0px #00000000
              `,
            }}
          >
            <img src={card.image} alt={card.title} className="w-fit h-fit object-cover rounded" />
            <div className="flex justify-between w-full h-fit">
              {/* Left: Name and Phone */}
              <div className="flex flex-col">
                {profileData?.firstName && (
                  <h3 className=" font-semibold text-neutral-950" style={{ fontSize: '14px' }}>
                    {profileData?.firstName} {profileData?.lastName}
                  </h3>
                )}
                {profileData?.phone && (
                  <div className="flex items-center">
                    <span className=" text-neutral-500 mt-[1px]" style={{ fontSize: '12px' }}>
                      {profileData?.phone}
                    </span>
                  </div>
                )}
              </div>
              {/* Right: Powered by logo */}
              <div className="flex flex-col items-end gap-1">
                <h4
                  className="text-neutral-400 uppercase mb-[2px] mt-[4px]"
                  style={{ fontSize: '6px', letterSpacing: '-0.01em' }}
                >
                  Powered by
                </h4>
                <div className="p-1 bg-black rounded-md flex items-center justify-center">
                  <img
                    alt="tap logo"
                    src="/logo-dark.png"
                    height={15}
                    width={15}
                    className="block"
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Right: Info */}
          <div className="flex flex-col w-full sm:w-[60%] py-auto border-l border-neutral-200 p-6 h-full min-h-[400px] max-h-[50vh]">
            <div className="flex flex-col flex-1 min-h-0 overflow-auto pr-2">
              {/* Tags */}
              <div className="flex items-start gap-2 mb-2 flex-wrap">
                {card.tags?.map((tag: any, idx: number) => (
                  <span
                    key={idx}
                    className={clsx(
                      'px-2 py-1 text-xs text-white rounded-[4px]',
                      getBadgeColor(tag.color),
                    )}
                  >
                    {tag.title}
                  </span>
                ))}
                <span className="text-xs bg-gray-200 px-2 py-1 rounded-[4px] text-gray-700 min-w-[48px] flex items-center justify-center">
                  {isModalLoading ? (
                    <Skeleton variant="text" width={32} height={16} />
                  ) : (
                    (viewCountOverride !== null ? viewCountOverride : card.views) + ' VIEWS'
                  )}
                </span>
              </div>
              {/* Title */}
              <h2 className="text-lg font-semibold mb-2">{card.title}</h2>
              {/* Description */}
              <div className="text-sm text-gray-700 space-y-2 max-h-[300px] overflow-auto">
                {parse(card.description || '')}
              </div>
              {/* Contact Section */}
              {card.contact && (
                <div className="mt-4 text-sm text-gray-700">
                  <p>
                    <strong>{card.contact?.name}</strong> 📞 {card.contact?.phone}
                  </p>
                  <p>{card.contact?.company}</p>
                  <p className="text-xs text-gray-500">Powered by {card.contact?.poweredBy}</p>
                </div>
              )}
              {/* Spacer to push referral link to bottom */}
              <div className="flex-1" />
              {referralCode ? (
                <>
                  {/* Referral Link pinned to bottom */}
                  <div className="mt-4 text-sm text-gray-700 space-y-1">
                    <p>
                      <strong>Referral Link:</strong>{' '}
                      <a
                        href={`https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${referralCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        https://ultra.go.link/ref?adj_t=1iwdprfa&rc={referralCode}
                      </a>
                    </p>
                  </div>
                </>
              ) : null}
              {/* Contact Section (again for redundancy) */}
              {card.contact && (
                <div className="mt-4 text-sm text-gray-700">
                  <p>
                    <strong>{card.contact?.name}</strong> 📞 {card.contact?.phone}
                  </p>
                  <p>{card.contact?.company}</p>
                  <p className="text-xs text-gray-500">Powered by {card.contact?.poweredBy}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-wrap justify-end gap-3 border-t border-neutral-200 p-5 bg-[#fafafa]">
          <button
            className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px] h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border hover:bg-[rgba(23,23,23,0.12)] transition-colors"
            style={{ lineHeight: '18px', color: '#0A0A0A' }}
            onClick={onWhatsAppShare}
          >
            <WhatsAppIcon sx={{ width: 14, height: 14.11 }} />
            <span className="font-semibold text-center text-xs">WhatsApp</span>
          </button>
          <button
            className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px] h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border hover:bg-[rgba(23,23,23,0.12)] transition-colors"
            style={{ lineHeight: '18px', color: '#0A0A0A' }}
            onClick={onEmailShare}
          >
            <DraftsOutlinedIcon sx={{ width: 14, height: 14.11 }} />
            <span className="font-semibold text-center text-xs">Email</span>
          </button>
          <button
            className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px] h-[38px] bg-[rgba(23,23,23,0.08)] rounded-[8px] box-border hover:bg-[rgba(23,23,23,0.12)] transition-colors"
            style={{ lineHeight: '18px', color: '#0A0A0A' }}
            onClick={onCopyToClipboard}
          >
            <ContentPasteIcon sx={{ width: 14, height: 14.11 }} />
            <span className="font-semibold text-center text-xs text-neutral-950">Copy</span>
          </button>
          <button
            className="flex flex-row justify-center items-center px-[15px] py-[10px] gap-[6px] h-[38px] bg-black rounded-[8px] box-border hover:bg-gray-800 transition-colors"
            style={{ lineHeight: '18px', color: 'white' }}
            onClick={onDownload}
          >
            <FileDownloadOutlinedIcon sx={{ width: 16, height: 16 }} />
            <span className="font-semibold text-center text-xs">Download</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingCreativeModal;
